import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useHistory, useLocation } from "react-router-dom";

interface ResetPasswordFormProps {
  onSwitchToLogin: () => void;
}

function useQueryParams() {
  const location = useLocation();
  return useMemo(() => new URLSearchParams(location.search), [location.search]);
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ onSwitchToLogin }) => {
  const history = useHistory();
  const query = useQueryParams();
  const initialEmail = query.get("email") || "";
  const initialToken = query.get("token") || query.get("reset_token") || "";

  const [email, setEmail] = useState(initialEmail);
  const [token, setToken] = useState(initialToken);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isVerifyingToken, setIsVerifyingToken] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const API_BASE_URL = `${process.env.REACT_APP_PUBLIC_BACKEND_URL}/api/user/auth`;

  useEffect(() => {
    const verifyToken = async () => {
      setIsVerifyingToken(true);
      setError(null);
      setMessage(null);

      if (!token) {
        setIsTokenValid(false);
        setError("Invalid reset link. Please request a new password reset email.");
        setIsVerifyingToken(false);
        return;
      }

      try {
        await axios.get(`${API_BASE_URL}/verify-reset-token`, {
          params: {
            token,
            reset_token: token,
            ...(email ? { email } : {}),
          },
          timeout: 15000,
        });

        setIsTokenValid(true);
      } catch (err) {
        const anyErr = err as any;
        const apiMessage = anyErr?.response?.data?.message;
        setIsTokenValid(false);
        setError(apiMessage || "This reset link is invalid or expired. Please request a new one.");
      } finally {
        setIsVerifyingToken(false);
      }
    };

    verifyToken();
  }, [API_BASE_URL, token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage(null);
    setError(null);

    if (!token) {
      setIsSubmitting(false);
      setError("Missing reset token. Please use the link from your email.");
      return;
    }
    if (newPassword.length < 8) {
      setIsSubmitting(false);
      setError("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setIsSubmitting(false);
      setError("Passwords do not match.");
      return;
    }

    try {
      const res = await axios.post(
        `${API_BASE_URL}/reset-password`,
        { email, reset_token: token, new_password: newPassword },
        { headers: { "Content-Type": "application/json" }, timeout: 15000 }
      );

      const apiMessage = res?.data?.message;
      setMessage(apiMessage || "Your password has been reset. You can now sign in.");
      setTimeout(() => {
        history.push("/login");
      }, 1200);
    } catch (err) {
      const anyErr = err as any;
      const apiMessage = anyErr?.response?.data?.message;
      setError(apiMessage || "Unable to reset password. The link may be invalid or expired.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      <div className="mb-6">
        <h2 className="text-3xl font-extrabold text-gray-900 dark:text-gray-100">Reset Password</h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          {isVerifyingToken ? "Verifying your reset link..." : "Set a new password for your account."}
        </p>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded transition-colors duration-200 mb-6">
          {error}
        </div>
      )}
      {message && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 px-4 py-3 rounded transition-colors duration-200 mb-6">
          {message}
        </div>
      )}

      {isVerifyingToken ? (
        <div className="text-sm text-gray-600 dark:text-gray-400">Please wait while we validate your link.</div>
      ) : isTokenValid ? (
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm transition-colors duration-200"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                New password
              </label>
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                autoComplete="new-password"
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm transition-colors duration-200"
                placeholder="Enter a new password"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Confirm new password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm transition-colors duration-200"
                placeholder="Re-enter the new password"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 dark:bg-indigo-500 hover:bg-indigo-700 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            {isSubmitting ? "Resetting..." : "Reset Password"}
          </button>
        </form>
      ) : null}

      <p className="mt-6 text-sm text-gray-600 dark:text-gray-400 text-center">
        Ready to sign in?{" "}
        <button onClick={onSwitchToLogin} className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300">
          Back to Sign In
        </button>
      </p>
    </div>
  );
};

export default ResetPasswordForm;
